import { useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import { currentPageAtom, pageTimeAtom } from '../store/index.atom';
import { useViewerRoute } from '../contexts/ViewerRoute';

import useFileActionInterval from './mutation/file/useFileActionInterval';
import useSetPageStayTime from './useSetPageStayTime';
import useTimer from './useTimer';
import usePageInOutIntervalRequest from './usePageInOutIntervalRequest';
import useIntervalAction from './useIntervalAction';

interface Props {
  isMobile: boolean;
}

const useViewerIntervalRequest = ({ isMobile }: Props) => {
  const { historyId, isBulk } = useViewerRoute();

  const { mutate } = useFileActionInterval(isBulk);

  const currentPage = useAtomValue(currentPageAtom);

  const [pageTime, setPageTime] = useAtom(pageTimeAtom);

  const { resetStart, getStayTime } = useTimer();

  useSetPageStayTime({ resetStart, getStayTime });

  const handleIntervalRequest = useCallback(() => {
    /*pageTime이 null인 경우는,
    유저가 페이지 이동을 하지 않았다는 의미로 
    현재 페이지에 대한 체류값을 추가해서 request를 보낸다

    null이 아닌 경우,
    유저가 페이지를 이동했다는 의미로 
    현재 페이지에 대한 체류시간을 추가해서 request를 보낸다 */
    const records =
      pageTime === null
        ? { [currentPage]: getStayTime() }
        : {
            ...pageTime,
            [currentPage]: getStayTime(),
          };

    mutate(
      {
        historyId,
        records,
      },
      {
        onSuccess: () => {
          resetStart();
          setPageTime(null);
        },
      },
    );
  }, [pageTime, mutate, currentPage, getStayTime, resetStart, setPageTime]);

  /*3초마다 request 요청을 보낸다 */
  const { startInterval, clearIntervalAction } = useIntervalAction({
    intervalCallbackAction: handleIntervalRequest,
  });

  /*유저가 페이지를 벗어났을 때 지금까지의 페이지 체류시간을 담아 request를 보낸다 
  다시 활성화가 되었을 때 타이머를 시작한다 */
  usePageInOutIntervalRequest({
    isMobile,
    handleInvisibleState: () => {
      handleIntervalRequest();
      clearIntervalAction();
    },
    handleVisibleState: () => {
      startInterval();
      resetStart();
    },
  });
};

export default useViewerIntervalRequest;
